import { useEffect } from 'react'
import { alert } from '@codeleap/modals'
import { ChatChannelInstance } from '../types'
import {
  setChannel,
  getChannel,
  getUser,
  getSelfLeaveChannel,
  setSelfLeaveChannel,
  forceChatRerender,
} from '../stores'

export type UseChatListenersOptions = {
  channel: ChatChannelInstance | null
  navigateToChatList?: (event: any) => void
}

export function useChatListeners({ channel, navigateToChatList }: UseChatListenersOptions) {
  useEffect(() => {
    if (!channel) return

    const handleUserRemoved = (event: any) => {
      const currentUserId = getUser()?.id
      const selfLeaveChannel = getSelfLeaveChannel()

      if (selfLeaveChannel === channel.id) {
        setSelfLeaveChannel(null)
        return
      }

      if (event.user?.id === currentUserId) {
        navigateToChatList?.(event)
        setChannel(null)

        alert.info({
          title: 'Removed from Chat',
          body: 'You have been removed from this chat by an administrator and no longer have access.',
        })
      }
    }

    const handleChannelDeleted = (event) => {
      setChannel(null)
      navigateToChatList?.(event)

      const wasDeletedByMe = event?.user?.id === getUser()?.id

      if (!wasDeletedByMe) {
        alert.info({
          title: 'Chat Deleted',
          body: 'This chat has been deleted and you no longer have access.',
        })
      }
    }

    const handleChannelUpdated = () => {
      const currentChannel = getChannel()

      if (currentChannel?.id === channel.id) {
        setChannel(channel)
      }
      forceChatRerender(channel.id)
    }

    channel.on('member.removed', handleUserRemoved)
    channel.on('channel.updated', handleChannelUpdated)
    channel.on('channel.deleted', handleChannelDeleted)

    return () => {
      channel.off('member.removed', handleUserRemoved)
      channel.off('channel.updated', handleChannelUpdated)
      channel.off('channel.deleted', handleChannelDeleted)
    }
  }, [channel, navigateToChatList])
}
